import { Input } from '@packages/ui/core';
import { Muted } from '@packages/ui/typography';
import { clsx } from 'clsx';
import { ChangeEvent, FocusEvent, KeyboardEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { HiInformationCircle, HiOutlineEye, HiOutlineEyeSlash } from 'react-icons/hi2';
import { useField } from 'remix-validated-form';
import {
  MIN_PASSWORD_LENGTH,
  PASSWORD_STRENGTH_COLORS,
  PASSWORD_STRENGTH_LABELS,
  evaluatePassword,
  type PasswordEvaluation,
} from '~/utils/password-strength';

type PasswordStrengthInputProps = {
  name: string;
  label: string;
  formId: string;
  userInputs?: string[];
  autoComplete?: string;
  defaultValue?: string;
};

export function PasswordStrengthInput({
  name,
  label,
  formId,
  userInputs = [],
  autoComplete = 'new-password',
  defaultValue,
}: PasswordStrengthInputProps) {
  const field = useField(name, { formId });
  const passwordInputProps = field.getInputProps() as ReturnType<typeof field.getInputProps> & {
    value?: string;
    defaultValue?: string;
    onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
    onKeyUp?: (event: KeyboardEvent<HTMLInputElement>) => void;
  };

  const passwordFieldValue = typeof passwordInputProps.value === 'string' ? (passwordInputProps.value as string) : undefined;
  const passwordDefaultValue =
    typeof passwordInputProps.defaultValue === 'string' ? (passwordInputProps.defaultValue as string) : defaultValue;

  const [passwordValue, setPasswordValue] = useState<string>(passwordFieldValue ?? passwordDefaultValue ?? '');
  const [debouncedPasswordValue, setDebouncedPasswordValue] = useState<string>(passwordFieldValue ?? passwordDefaultValue ?? '');
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isCapsLockOn, setIsCapsLockOn] = useState(false);

  useEffect(() => {
    if (passwordFieldValue != null) {
      setPasswordValue(passwordFieldValue);
    }
  }, [passwordFieldValue]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedPasswordValue(passwordValue);
    }, 200);

    return () => {
      clearTimeout(timer);
    };
  }, [passwordValue]);

  const normalizedUserInputs = useMemo(() => {
    return Array.from(
      new Set(
        userInputs
          .map(value => (typeof value === 'string' ? value.trim().toLowerCase() : ''))
          .filter((value): value is string => Boolean(value)),
      ),
    );
  }, [userInputs]);

  const passwordEvaluation = useMemo<PasswordEvaluation | null>(() => {
    if (!debouncedPasswordValue) {
      return null;
    }

    return evaluatePassword(debouncedPasswordValue, normalizedUserInputs);
  }, [debouncedPasswordValue, normalizedUserInputs]);

  const passwordScore = passwordEvaluation?.score ?? 0;
  const activeStrengthClass = PASSWORD_STRENGTH_COLORS[Math.min(passwordScore, PASSWORD_STRENGTH_COLORS.length - 1)];
  const passwordStrengthLabel = PASSWORD_STRENGTH_LABELS[passwordScore];

  // Extract first feedback message (warning takes priority, then first suggestion) - matches iOS behavior
  const feedbackMessage = useMemo(() => {
    // Show length requirement if password is empty or too short
    if (!passwordValue || passwordValue.length < MIN_PASSWORD_LENGTH) {
      return `Password must be at least ${MIN_PASSWORD_LENGTH} characters`;
    }

    if (!passwordEvaluation?.feedback) {
      return null;
    }

    // Show warning if present
    if (passwordEvaluation.feedback.warning) {
      return passwordEvaluation.feedback.warning;
    }

    // Otherwise show first suggestion if present
    if (passwordEvaluation.feedback.suggestions && passwordEvaluation.feedback.suggestions.length > 0) {
      return passwordEvaluation.feedback.suggestions[0];
    }

    return null;
  }, [passwordValue, passwordEvaluation]);

  const {
    onBlur: passwordOnBlur,
    onChange: passwordOnChange,
    onKeyDown: passwordOnKeyDown,
    onKeyUp: passwordOnKeyUp,
    ...restPasswordInputProps
  } = passwordInputProps;

  const handlePasswordChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setPasswordValue(event.target.value);
      passwordOnChange?.(event);
    },
    [passwordOnChange],
  );

  const handlePasswordKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      passwordOnKeyDown?.(event);
      setIsCapsLockOn(event.getModifierState?.('CapsLock') ?? false);
    },
    [passwordOnKeyDown],
  );

  const handlePasswordKeyUp = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      passwordOnKeyUp?.(event);
      setIsCapsLockOn(event.getModifierState?.('CapsLock') ?? false);
    },
    [passwordOnKeyUp],
  );

  const handlePasswordBlur = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      setIsCapsLockOn(false);
      passwordOnBlur?.(event);
    },
    [passwordOnBlur],
  );

  const handleTogglePasswordVisibility = useCallback(() => {
    setIsPasswordVisible(previous => !previous);
  }, []);

  return (
    <Input.Root
      {...restPasswordInputProps}
      type={isPasswordVisible ? 'text' : 'password'}
      onChange={handlePasswordChange}
      onBlur={handlePasswordBlur}
      onKeyDown={handlePasswordKeyDown}
      onKeyUp={handlePasswordKeyUp}
      autoComplete={autoComplete}
    >
      <Input.Header>
        <Input.Label>{label}</Input.Label>
      </Input.Header>

      <Input.Field
        isRightInteractive
        right={
          <button
            type="button"
            onClick={handleTogglePasswordVisibility}
            className="focus-visible:ring-primary-500 text-gray-600 transition-colors hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
            aria-label={isPasswordVisible ? 'Hide password' : 'Show password'}
          >
            {isPasswordVisible ? <HiOutlineEyeSlash /> : <HiOutlineEye />}
          </button>
        }
      />

      <div className="mt-3 space-y-3" aria-live="polite">
        <div>
          <div className="flex h-1 space-x-1">
            {[0, 1, 2, 3, 4].map(index => (
              <div
                key={index}
                className={clsx('h-full flex-1 rounded-full transition-colors', {
                  [activeStrengthClass]: index <= passwordScore,
                  'bg-gray-200': index > passwordScore,
                })}
              />
            ))}
          </div>
          <div className="mt-2 text-sm">
            <Muted className="font-medium">Strength: {passwordStrengthLabel}</Muted>{' '}
            {isCapsLockOn && (
              <span className="text-danger font-medium" role="status" aria-live="assertive">
                • Caps Lock is on
              </span>
            )}
          </div>
        </div>

        <div className="min-h-[1.5rem]">
          {feedbackMessage && (
            <div className="flex items-start gap-2 text-sm">
              <HiInformationCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-600" />
              <span className="text-gray-600">{feedbackMessage}</span>
            </div>
          )}
        </div>
      </div>
    </Input.Root>
  );
}
